import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';

import extractCodeError from 'auth/utils';
import usePublicApi from 'public-api';
import { getFieldErrors, isResponse } from 'public-api/utils';
import appRoutes from 'routes/codegen/appRoutes';
import authRoutes from 'routes/codegen/authRoutes';
import { useAsyncFunction } from 'stores/hooks';
import { CodeInput } from 'ui';
import AuthCard from 'ui-brand/components/AuthCard';
import Button from 'ui-brand/components/Button';
import { EmailIcon } from 'ui-brand/components/icons';
import PasswordInput from 'ui-brand/components/PasswordInput';
import TextInput from 'ui-brand/components/TextInput';

type TwoFactorState = {
  token: string;
  email: string;
};

type LoginErrors = {
  email?: string;
  password?: string;
  error?: string;
};

function Login() {
  const [errors, setErrors] = useState<LoginErrors>({});
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const publicApi = usePublicApi();

  const [twoFactorState, setTwoFactorState] = useState<TwoFactorState | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [useBackupCode, setUseBackupCode] = useState(false);

  const next = new URLSearchParams(window.location.search).get('next');
  const lang = window.appState.lang;

  const redirectAfterLogin = () => {
    if (next) window.location.replace(next);
    else window.location.href = appRoutes.index();
  };

  const login = async () => {
    setErrors({});
    try {
      const data = await publicApi.AuthLoginApi.create({ request: { email, password } });
      if (data.two_factor_required && data.token) {
        setTwoFactorState({ token: data.token, email });
        return;
      }
      redirectAfterLogin();
    } catch (error) {
      if (isResponse(error)) {
        setErrors(await getFieldErrors(error));
      }
    }
  };

  const verifyTwoFactor = async () => {
    if (!twoFactorState) return;
    setTwoFactorError(null);
    try {
      await publicApi.AuthTwoFactorVerifyApi.create({
        request: { token: twoFactorState.token, code: twoFactorCode },
      });
      redirectAfterLogin();
    } catch (error) {
      setTwoFactorError(await extractCodeError(error, gettext('Verification failed. Please try again.')));
    }
  };

  const [handleLogin, isLoggingIn] = useAsyncFunction(login);
  const [handleVerify, isVerifying] = useAsyncFunction(verifyTwoFactor);

  const switchCodeType = (backup: boolean) => {
    setUseBackupCode(backup);
    setTwoFactorCode('');
    setTwoFactorError(null);
  };

  const handleBackToLogin = () => {
    setTwoFactorState(null);
    setPassword('');
    switchCodeType(false);
  };

  if (twoFactorState) {
    return (
      <AuthCard
        title={gettext('Two-Factor Authentication')}
        subtitle={
          useBackupCode
            ? gettext('Enter one of your backup codes.')
            : gettext('Enter the 6-digit code from your authenticator app.')
        }
      >
        <div className="mt-8 flex flex-col gap-5">
          <CodeInput
            key={useBackupCode ? 'backup' : 'app'}
            length={useBackupCode ? 8 : 6}
            alphanumeric={useBackupCode}
            value={twoFactorCode}
            onChange={setTwoFactorCode}
            error={twoFactorError || undefined}
            autoFocus
            onComplete={handleVerify}
          />
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleVerify}
            loading={isVerifying}
            disabled={twoFactorCode.length === 0}
          >
            {gettext('Verify')}
          </Button>
          <button
            type="button"
            onClick={() => switchCodeType(!useBackupCode)}
            className="text-sm font-semibold text-brand-500"
          >
            {useBackupCode ? gettext('Use authenticator app instead') : gettext('Use backup code instead')}
          </button>
          <button type="button" onClick={handleBackToLogin} className="text-sm text-retino-darkgrey underline">
            {gettext('Back to login')}
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={gettext('Sign in to Retino')} subtitle={gettext('Pick up where you left off.')}>
      <form
        className="mt-8 flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          handleLogin();
        }}
      >
        <TextInput
          label={gettext('Work email')}
          type="email"
          name="email"
          autoComplete="email"
          icon={<EmailIcon />}
          placeholder="name@company.com"
          value={email}
          onChange={setEmail}
          error={errors.email}
        />
        <div>
          <PasswordInput
            label={gettext('Password')}
            name="password"
            placeholder={gettext('Your password')}
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
            error={errors.password}
          />
          <div className="mt-3 text-right">
            <Link
              to={authRoutes.authPasswordReset({ lang })}
              className="text-base font-semibold text-brand-500 sm:text-sm"
            >
              {gettext('Forgot your password?')}
            </Link>
          </div>
        </div>
        {errors.error && (
          <p role="alert" className="text-center text-sm text-red-600">
            {errors.error}
          </p>
        )}
        <Button type="submit" variant="primary" size="lg" fullWidth loading={isLoggingIn}>
          {gettext('Sign in')}
        </Button>
      </form>
      <p className="mt-8 text-center text-base text-retino-darkgrey">
        {gettext("Don't have an account?")}{' '}
        <Link
          to={authRoutes.onboarding({ lang }) + (next ? `?next=${next}` : '')}
          className="font-semibold text-brand-500"
        >
          {gettext('Try for free')}
        </Link>
      </p>
    </AuthCard>
  );
}

export default observer(Login);
